/**
 * Seeded pseudo-random helpers — THE deterministic RNG for the web app and
 * the shared planners.
 *
 * One implementation on purpose: renderers and texture generators that want
 * per-object "random" variation must reproduce the exact same output on every
 * re-render (and across cache lookups), so an ad-hoc `Math.random()` — or a
 * second, slightly different generator — is a repaint flicker or a cache miss
 * waiting to happen. Extracted from woodTextureGenerator.js, which pioneered
 * the pattern (Lehmer / Park–Miller minimal standard generator).
 */

/**
 * Lehmer (Park–Miller) PRNG. Returns a function yielding floats in [0, 1).
 * Same seed → same sequence, on every platform.
 *
 * @param {number} seed  any integer; 0 is nudged so the stream can't collapse
 * @returns {() => number}
 */
function seededRandom(seed) {
  let s = seed;
  return () => {
    s = (s * 16807 + 0) % 2147483647;
    return (s - 1) / 2147483646;
  };
}

/**
 * Deterministic 32-bit hash of a string (FNV-1a), for seeding `seededRandom`
 * from ids / cache keys: `seededRandom(hashStringToSeed(object.id))` gives each
 * object its own stable variation with no stored seed field.
 *
 * @param {string} str
 * @returns {number} unsigned 32-bit integer
 */
function hashStringToSeed(str) {
  let h = 0x811c9dc5;
  for (let i = 0; i < (str || '').length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

module.exports = { seededRandom, hashStringToSeed };
